import type { ComponentType, SVGProps } from "react";
import { NavLink } from "react-router-dom";

import styles from "./NavItem.module.css";

interface BaseProps {
  label: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  collapsed?: boolean;
}

type NavItemProps = BaseProps &
  ({ to: string; onClick?: never } | { to?: never; onClick: () => void });

/**
 * A single sidebar entry. Renders a router link when given `to`, or a button
 * when given `onClick` (e.g. Settings, which has no route yet). Collapsing the
 * sidebar hides the label and centres the icon. The visual style is the shared
 * baseline every navigation surface reuses.
 */
export function NavItem({ label, icon: Icon, collapsed, ...rest }: NavItemProps) {
  const className = collapsed ? `${styles.item} ${styles.collapsed}` : styles.item;

  if ("to" in rest && rest.to !== undefined) {
    return (
      <NavLink
        to={rest.to}
        className={({ isActive }) => (isActive ? `${className} ${styles.active}` : className)}
        title={collapsed ? label : undefined}
      >
        <Icon />
        <span className={styles.label}>{label}</span>
      </NavLink>
    );
  }

  return (
    <button
      type="button"
      className={className}
      onClick={rest.onClick}
      title={collapsed ? label : undefined}
    >
      <Icon />
      <span className={styles.label}>{label}</span>
    </button>
  );
}
